import React, { lazy, Suspense, useEffect, useRef, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { useVelaStore, VelaStore } from '../store'
import widgetStyles from '../styles/widget.scss?inline'

// Lazy load components to keep bundle size small
const ChatUI = lazy(() => import('./ChatUI'))
const SettingsDialog = lazy(() => import('./SettingsDialog'))

export interface VelaWidgetProps {
  container: HTMLElement
  initialState?: Partial<Omit<VelaStore, 'open' | 'close' | 'toggle'>>
  onMount?: () => void
}

export const VelaWidget: React.FC<VelaWidgetProps> = ({ container, initialState, onMount }) => {
  const shadowRootRef = useRef<ShadowRoot | null>(null)

  // Wrap 'useVelaStore' inside useState() to make sure initialState override is called only once
  const [store] = useState(() => {
    // Override default initial state if provided
    if (initialState) {
      const currentState = useVelaStore.getState()

      useVelaStore.setState({
        ...currentState,
        ...initialState,
      })
    }

    return useVelaStore
  })

  // Effect to create the shadow root and mount the widget
  useEffect(() => {
    // Variable to hold the unsubscribe function
    let unsubscribe: (() => void) | undefined

    // Create shadow root if it doesn't exist
    if (!shadowRootRef.current) {
      // Create a div to attach the shadow root to
      const shadowHost = document.createElement('div')
      shadowHost.className = 'vela-widget-host'
      container.appendChild(shadowHost)

      // Create shadow root
      shadowRootRef.current = shadowHost.attachShadow({ mode: 'open' })

      // Create a container for the React root
      const rootElement = document.createElement('div')
      rootElement.className = 'vela-widget-root'
      shadowRootRef.current.appendChild(rootElement)

      // Add base styles to the shadow root
      const styleElement = document.createElement('style')
      styleElement.textContent = widgetStyles
      shadowRootRef.current.appendChild(styleElement)

      const root = createRoot(rootElement)

      // Initial render
      root.render(<WidgetContent />)

      // Should manually send zustand store event to the react component in shadow dom
      unsubscribe = store.subscribe(() => {
        root.render(<WidgetContent />)
      })

      if (onMount) {
        onMount()
      }
    }

    // Cleanup function
    return () => {
      // Remove the shadow host when the component unmounts
      const shadowHost = container.querySelector('.vela-widget-host')
      if (shadowHost) {
        container.removeChild(shadowHost)
      }

      // Unsubscribe from store changes
      if (typeof unsubscribe === 'function') {
        unsubscribe()
      }

      shadowRootRef.current = null
    }
  }, [container, onMount, store])

  // Return null because the actual rendering happens in the shadow DOM
  return null
}

const WidgetContent: React.FC = () => {
  const isOpen = useVelaStore(state => state.isOpen)
  const open = useVelaStore(state => state.open)
  const close = useVelaStore(state => state.close)
  const settings = useVelaStore(state => state.settings)
  const messages = useVelaStore(state => state.messages)
  const sessionId = useVelaStore(state => state.sessionId)

  // Use local state for settings dialog visibility
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)

  // Toggle settings dialog visibility
  const toggleSettings = (): void => {
    setIsSettingsOpen(prev => !prev)
  }

  if (!isOpen) {
    return (
      <button
        onClick={() => {
          open()
        }}
        style={{
          position: 'fixed',
          bottom: '90px',
          right: '20px',
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          backgroundColor: 'var(--vela-primary-color)',
          color: 'white',
          border: 'none',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          fontSize: '24px',
          zIndex: 9999,
        }}
      >
        💬
      </button>
    )
  }

  // Get Langfuse session URL if available
  const getLangfuseSessionUrl = (): string | null => {
    if (messages.length > 0 && sessionId) {
      return `${settings.langfuseBaseUrl}/sessions/${sessionId}`
    }
    return null
  }

  const langfuseSessionUrl = getLangfuseSessionUrl()

  // Determine if we're on mobile
  const isMobile = window.innerWidth <= 768

  // Set widget dimensions based on device
  const widgetStyle: React.CSSProperties = {
    position: 'fixed',
    top: isMobile ? '0' : 'auto',
    right: '20px',
    bottom: isMobile ? '0' : '20px',
    left: isMobile ? '0' : 'auto',
    width: isMobile ? '100%' : '400px',
    height: isMobile ? '100%' : '500px',
    maxWidth: '100%',
    maxHeight: '100%',
    zIndex: 9999,
  }

  return (
    <div className={`vela-widget theme-light`} style={widgetStyle}>
      <div className="vela-widget-header">
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <h2 style={{ margin: 0 }}>Chat Assistant</h2>
          {settings.langfuseDebugMode && langfuseSessionUrl && (
            <a
              href={langfuseSessionUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontSize: '10px',
                marginLeft: '8px',
                background: 'white',
                color: 'black',
                cursor: 'pointer',
                borderRadius: '4px',
                border: 'none',
                boxShadow: 'none',
                padding: '2px 6px',
                textDecoration: 'none',
                display: 'inline-block',
              }}
            >
              Langfuse session
            </a>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <button
            onClick={() => toggleSettings()}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '16px',
              marginRight: '10px',
            }}
            title="Settings"
          >
            ⚙️
          </button>
          <button
            onClick={() => close()}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '20px',
            }}
            title="Minimize"
          >
            −
          </button>
        </div>
      </div>

      <div
        className="vela-widget-content"
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          padding: 0, // Remove padding to give more space to the chat UI
          position: 'relative', // For absolute positioning of the settings dialog
        }}
      >
        <Suspense fallback={<div>Loading...</div>}>
          <ChatUI />
          {isSettingsOpen ? <SettingsDialog onClose={() => setIsSettingsOpen(false)} /> : null}
        </Suspense>
      </div>
    </div>
  )
}
